import React, { useState, useEffect } from 'react';
import {
    TextField,
    Button,
    Typography,
    Switch,
    FormControlLabel,
    Snackbar,
    Alert,
    AlertColor,
    Box,
    Chip,
    Checkbox,
    FormGroup,
    CircularProgress,
    Paper,
    Divider,
} from '@mui/material';
import { userService, UserProfile } from '../services';
import LanguageSwitcher from './LanguageSwitcher';
import { useTranslation } from 'react-i18next';
import ErrorBoundary from './ErrorBoundary';

const Profile: React.FC = () => {
    const { t } = useTranslation();
    const [profile, setProfile] = useState<UserProfile | null>(null);
    const [newAllergy, setNewAllergy] = useState('');
    const [snackbar, setSnackbar] = useState<{
        open: boolean;
        message: string;
        severity: AlertColor;
    }>({
        open: false,
        message: '',
        severity: 'success'
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                setLoading(true);
                const profileData = await userService.getUserProfile();
                setProfile(profileData);
            } catch (err) {
                console.error('Failed to fetch user profile:', err);
                setSnackbar({
                    open: true,
                    message: t('profileComponent.loadError'),
                    severity: 'error'
                });
            } finally {
                setLoading(false);
            }
        };
        fetchProfile();
    }, [t]);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!profile) return;

        const { name, value, checked } = event.target;
        if (name.startsWith('dietaryPreferences.')) {
            const preference = name.split('.')[1] as keyof UserProfile['dietaryPreferences'];
            setProfile(prev => prev ? {
                ...prev,
                dietaryPreferences: {
                    ...prev.dietaryPreferences,
                    [preference]: checked,
                },
            } : null);
        } else {
            setProfile(prev => prev ? ({
                ...prev,
                [name]: name === 'notificationsEnabled' ? checked : value
            }) : null);
        }
    };

    const handleAddAllergy = () => {
        if (!profile || !newAllergy.trim()) return;

        const normalizedAllergy = newAllergy.trim().toLowerCase();
        if (!profile.allergies.includes(normalizedAllergy)) {
            setProfile(prev => prev ? ({
                ...prev,
                allergies: [...prev.allergies, normalizedAllergy],
            }) : null);
            setNewAllergy('');
        }
    };

    const handleKeyPress = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter') {
            handleAddAllergy();
        }
    };

    const handleRemoveAllergy = (allergyToRemove: string) => {
        if (!profile) return;

        setProfile(prev => prev ? ({
            ...prev,
            allergies: prev.allergies.filter(allergy => allergy !== allergyToRemove),
        }) : null);
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!profile) return;

        try {
            setSaving(true);
            await userService.updateUserProfile(profile);
            setSnackbar({
                open: true,
                message: t('profileComponent.updateSuccess'),
                severity: 'success'
            });
        } catch (error) {
            console.error('Failed to update profile:', error);
            setSnackbar({
                open: true,
                message: t('profileComponent.updateError'),
                severity: 'error'
            });
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
                <CircularProgress />
            </Box>
        );
    }

    if (!profile) {
        return (
            <Alert severity="error">
                {t('profileComponent.loadError')}
            </Alert>
        );
    }

    return (
        <ErrorBoundary>
            <Box sx={{ maxWidth: 600, margin: 'auto', mt: 4 }}>
                <Paper elevation={3} sx={{ p: 4 }}>
                    <Typography variant="h4" component="h1" gutterBottom>
                        {t('profileComponent.title')}
                    </Typography>

                    <form onSubmit={handleSubmit}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            {/* Basic Information */}
                            <TextField
                                fullWidth
                                label={t('profileComponent.name')}
                                name="name"
                                value={profile.name}
                                onChange={handleChange}
                                required
                                disabled={saving}
                            />
                            <TextField
                                fullWidth
                                label={t('profileComponent.email')}
                                name="email"
                                type="email"
                                value={profile.email}
                                onChange={handleChange}
                                required
                                disabled={saving}
                            />

                            <Divider sx={{ my: 2 }} />

                            {/* Language Selection */}
                            <Box>
                                <Typography variant="h6" gutterBottom>
                                    {t('language')}
                                </Typography>
                                <LanguageSwitcher />
                            </Box>

                            <Divider sx={{ my: 2 }} />

                            {/* Allergies Section */}
                            <Box>
                                <Typography variant="h6" gutterBottom>
                                    {t('profileComponent.allergies.title')}
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                                    {profile.allergies.map(allergy => (
                                        <Chip
                                            key={allergy}
                                            label={allergy}
                                            onDelete={() => handleRemoveAllergy(allergy)}
                                            disabled={saving}
                                        />
                                    ))}
                                </Box>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <TextField
                                        label={t('profileComponent.allergies.addNew')}
                                        value={newAllergy}
                                        onChange={(e) => setNewAllergy(e.target.value)}
                                        onKeyPress={handleKeyPress}
                                        disabled={saving}
                                        size="small"
                                    />
                                    <Button
                                        onClick={handleAddAllergy}
                                        variant="contained"
                                        disabled={!newAllergy.trim() || saving}
                                    >
                                        {t('profileComponent.allergies.add')}
                                    </Button>
                                </Box>
                            </Box>

                            <Divider sx={{ my: 2 }} />

                            {/* Dietary Preferences */}
                            <Box>
                                <Typography variant="h6" gutterBottom>
                                    {t('profileComponent.dietaryPreferences.title')}
                                </Typography>
                                <FormGroup>
                                    {Object.entries(profile.dietaryPreferences).map(([key, value]) => (
                                        <FormControlLabel
                                            key={key}
                                            control={
                                                <Checkbox
                                                    checked={value}
                                                    onChange={handleChange}
                                                    name={`dietaryPreferences.${key}`}
                                                    disabled={saving}
                                                />
                                            }
                                            label={t(`profileComponent.dietaryPreferences.${key}`)}
                                        />
                                    ))}
                                </FormGroup>
                            </Box>

                            <Divider sx={{ my: 2 }} />

                            {/* Notifications */}
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={profile.notificationsEnabled}
                                        onChange={handleChange}
                                        name="notificationsEnabled"
                                        color="primary"
                                        disabled={saving}
                                    />
                                }
                                label={t('profileComponent.notifications.enable')}
                            />

                            {/* Submit Button */}
                            <Button
                                type="submit"
                                variant="contained"
                                color="primary"
                                disabled={saving}
                                sx={{ mt: 2 }}
                            >
                                {saving ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <CircularProgress size={20} color="inherit" />
                                        {t('profileComponent.updateProfile')}
                                    </Box>
                                ) : (
                                    t('profileComponent.updateProfile')
                                )}
                            </Button>
                        </Box>
                    </form>
                </Paper>

                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={6000}
                    onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                >
                    <Alert
                        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                        severity={snackbar.severity}
                        sx={{ width: '100%' }}
                    >
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </Box>
        </ErrorBoundary>
    );
};

export default Profile;